// Admin console: the show schedule. The hub plays the street's songs by itself
// during these hours (in order, repeating, or shuffled), so no computer has to
// stay open; and "Street speakers" plays whatever the street is playing on
// this device, for the computer or phone wired to the outdoor speakers.

import type { LibraryListing } from "../shared/library.ts";
import type { NowPlaying } from "../shared/protocol.ts";
import { DEFAULT_SCHEDULE, describeHours, type ShowSchedule } from "../shared/schedule.ts";
import { setupListen } from "./live.ts";
import { $, api, el, toast } from "./site.ts";

interface Status {
  running: boolean;
  nowPlaying: NowPlaying | null;
  otherPlaying: NowPlaying | null;
  nextStart: number | null;
  showEnds: number | null;
}

interface Reply {
  schedule: ShowSchedule;
  status: Status;
}

// Monday first, as people read a week.
const DAYS: [number, string][] = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [0, "Sun"]];
const POLL_MS = 5000;

let schedule: ShowSchedule = { ...DEFAULT_SCHEDULE };
let status: Status | null = null;
let songs: LibraryListing[] = [];
// Days and songs as edited, before saving.
let days = new Set<number>();
let chosen = new Set<string>();
let editing = false;

const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_SCHEDULE.timeZone;

function when(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).replace(" ", "").toLowerCase();
  if (d.toDateString() === today.toDateString()) return `today at ${time}`;
  if (d.toDateString() === tomorrow.toDateString()) return `tomorrow at ${time}`;
  return `${d.toLocaleDateString([], { weekday: "long" })} at ${time}`;
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).replace(" ", "").toLowerCase();
}

function renderStatus(): void {
  const box = $("#schedule-status");
  const s = status;
  let text: string;
  if (!schedule.enabled) text = "The schedule is off. Turn it on and save to run the show by itself.";
  else if (!s) text = "Checking…";
  else if (s.running && s.nowPlaying) {
    text = `Playing now: ${s.nowPlaying.title}.` + (s.showEnds ? ` Tonight's show runs until ${clock(s.showEnds)}.` : "");
  } else if (s.running && s.otherPlaying) {
    text = `You're playing ${s.otherPlaying.title} from the Music panel; the schedule carries on after it.`;
  } else if (s.running) {
    text = playable().length ? "Starting the next song…" : "Show hours are on, but there are no analyzed songs turned on. Add or turn on songs in Music.";
  } else if (schedule.pausedUntil && schedule.pausedUntil > Date.now()) {
    text = `Stopped for tonight.` + (s.nextStart ? ` The next show starts ${when(s.nextStart)}.` : "");
  } else {
    text = s.nextStart ? `The next show starts ${when(s.nextStart)}.` : "No show coming up: pick some days.";
  }
  box.textContent = text;
  $("#schedule-skip").hidden = !s?.nowPlaying;
  $("#schedule-stop").hidden = !s?.running;
  $("#schedule-resume").hidden = !(schedule.enabled && schedule.pausedUntil && schedule.pausedUntil > Date.now());
}

const playable = () => songs.filter((s) => s.analysis && s.enabled);

function renderForm(): void {
  $<HTMLInputElement>("#schedule-enabled").checked = schedule.enabled;
  $<HTMLInputElement>("#schedule-start").value = schedule.start;
  $<HTMLInputElement>("#schedule-end").value = schedule.end;
  for (const radio of document.querySelectorAll<HTMLInputElement>('input[name="schedule-order"]')) {
    radio.checked = radio.value === schedule.order;
  }
  days = new Set(schedule.days);
  chosen = new Set(schedule.songIds);
  $<HTMLInputElement>("#schedule-all-songs").checked = schedule.songIds.length === 0;
  renderDays();
  renderSongs();
  renderPreview();
  const zone = schedule.timeZone !== browserZone && schedule.enabled
    ? `Times are in ${schedule.timeZone}; saving here switches them to this device's time zone, ${browserZone}.`
    : `Times are in ${browserZone}.`;
  $("#schedule-zone").textContent = zone;
}

function renderDays(): void {
  $("#schedule-days").replaceChildren(
    ...DAYS.map(([day, name]) => {
      const b = el("button", { type: "button", class: "day-chip", "aria-pressed": String(days.has(day)) }, name);
      b.addEventListener("click", () => {
        if (days.has(day)) days.delete(day);
        else days.add(day);
        edited();
        renderDays();
      });
      return b;
    }),
  );
}

function renderSongs(): void {
  const all = $<HTMLInputElement>("#schedule-all-songs").checked;
  const list = $("#schedule-songs");
  list.hidden = all;
  list.replaceChildren(
    ...songs.map((song) => {
      const box = el("input", { type: "checkbox" }) as HTMLInputElement;
      box.checked = chosen.has(song.id);
      box.disabled = !song.analysis;
      box.addEventListener("change", () => {
        if (box.checked) chosen.add(song.id);
        else chosen.delete(song.id);
        edited();
      });
      return el(
        "li",
        {},
        el(
          "label",
          { class: "inline" },
          box,
          " ",
          song.title,
          !song.analysis
            ? el("span", { class: "hint" }, " (analyze it first)")
            : song.enabled
              ? ""
              : el("span", { class: "hint" }, " (turned off in Music: skipped)"),
        ),
      );
    }),
  );
}

function formSchedule(): ShowSchedule {
  const order = document.querySelector<HTMLInputElement>('input[name="schedule-order"]:checked')?.value === "shuffle" ? "shuffle" : "loop";
  const all = $<HTMLInputElement>("#schedule-all-songs").checked;
  return {
    enabled: $<HTMLInputElement>("#schedule-enabled").checked,
    start: $<HTMLInputElement>("#schedule-start").value || DEFAULT_SCHEDULE.start,
    end: $<HTMLInputElement>("#schedule-end").value || DEFAULT_SCHEDULE.end,
    days: [...days].sort(),
    order,
    // Chosen songs play in the Music list's order.
    songIds: all ? [] : songs.filter((s) => chosen.has(s.id)).map((s) => s.id),
    timeZone: browserZone,
  };
}

// What the public page will say, as the admin edits.
function renderPreview(): void {
  const s = formSchedule();
  let text = "";
  try {
    text = `The public page will say: "${describeHours(s)}"`;
  } catch {
    text = "";
  }
  if (s.start === s.end) text = "The show needs to end at a different time than it starts.";
  else if (s.end < s.start) text += " (ends after midnight)";
  $("#schedule-preview").textContent = text;
}

function edited(): void {
  editing = true;
  renderPreview();
}

async function load(): Promise<void> {
  try {
    const reply = await api<Reply>("GET", "/api/admin/schedule");
    status = reply.status;
    // Don't overwrite what the admin is in the middle of changing.
    if (!editing) {
      schedule = reply.schedule;
      renderForm();
    } else {
      schedule = { ...schedule, pausedUntil: reply.schedule.pausedUntil };
    }
  } catch {
    status = null;
  }
  renderStatus();
}

async function loadSongs(): Promise<void> {
  try {
    songs = (await api<{ songs: LibraryListing[] }>("GET", "/api/library")).songs;
  } catch {
    songs = [];
  }
  renderSongs();
}

async function act(path: string, method = "POST", body?: unknown): Promise<void> {
  try {
    const reply = await api<Reply>(method, `/api/admin/schedule${path}`, body);
    schedule = reply.schedule;
    status = reply.status;
    renderStatus();
  } catch (e) {
    toast((e as Error).message);
  }
}

export function setupSchedule(): void {
  $("#schedule-panel").hidden = false;
  const form = $<HTMLFormElement>("#schedule-form");
  form.addEventListener("input", edited);
  $("#schedule-all-songs").addEventListener("change", () => {
    // Switching to a chosen list starts from every analyzed song.
    if (!$<HTMLInputElement>("#schedule-all-songs").checked && chosen.size === 0) {
      chosen = new Set(playable().map((s) => s.id));
    }
    renderSongs();
  });
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const next = formSchedule();
    if (next.enabled && next.days.length === 0) return toast("Pick at least one day for the show.");
    if (!$<HTMLInputElement>("#schedule-all-songs").checked && next.songIds.length === 0) {
      return toast("Pick at least one song, or choose all songs.");
    }
    try {
      const reply = await api<Reply>("PUT", "/api/admin/schedule", next);
      editing = false;
      schedule = reply.schedule;
      status = reply.status;
      renderForm();
      renderStatus();
      toast(schedule.enabled ? "Schedule saved. The show will run by itself." : "Schedule saved (off).");
    } catch (err) {
      toast((err as Error).message);
    }
  });
  $("#schedule-skip").addEventListener("click", () => void act("/skip"));
  $("#schedule-stop").addEventListener("click", () => {
    if (confirm("Stop the show for tonight? The lights and music stop now, and the show starts again at its next start time.")) {
      void act("/stop-tonight");
    }
  });
  $("#schedule-resume").addEventListener("click", () => void act("/resume"));

  // The speaker player: the street's music on this device, in step.
  setupListen($("#speakers"), { speakers: true });

  void loadSongs().then(load);
  setInterval(() => void load(), POLL_MS);
  // The song list changes when songs are added or analyzed.
  setInterval(() => void loadSongs(), 60_000);
}
